<template lang="pug">
ResultsContainer.search-results-container(v-bind="containerAttrs")
  template(#left)
    .suggestion-container
      .no-results(v-if="emptySearchResults && initialLoad")
        h3 {{__("No Results")}}
      .title(v-if="emptySearchResults && initialLoad") {{ __('Top Suggestions') }}
      .products(v-bind="previewSearchContainerAttrs")
        rogue-link.product(
          :to="product.url || ''"
          :productId="product.hasRedirect ? '' : product.productId"
          v-for="(product,index) in products"
          :key="product.productId"
          @click="trackClick(product, index)"
          :class="!product.productId && 'loading'"
          :data-cnstrc-item="previewSearchMeta ? 'recommendation' : undefined"
          :data-cnstrc-item-section="SECTIONS.PRODUCTS"
          :data-cnstrc-item-id="product.productId"
          :data-cnstrc-item-name="product.name"
          :data-cnstrc-item-price="product.special_price && product.price ? Math.min(product.special_price, product.price) : product.price || undefined"
        )
          StaticImage(:src='product.thumbnail' :width="200" :alt='product.name' lazy)
          .name(v-html='product.name || ""')
          template(v-if="!product.hidePrice")
            Price(v-if="!product.quoted" :product="product" theme="search")
            .quoted-price.font-weight-bold(v-else) {{ __('Quote Available') }}
      .links
        rogue-link.view-all(
          v-if="!emptySearchResults && query"
          :to="'/search?q='+encodeURIComponent(query)"
        ) {{__('View All')}}
        rogue-link.invitational(
          v-if="invitationalQuery" to="/invitational"
          :data-cnstrc-item-section="SECTIONS.PAGES"
          data-cnstrc-item-name="Rogue Invitational"
        )
          StaticImage(:width="275" :height="200" alt="" src="/Rogue%20Invitational/Invitational_Nav_Search_2_xqhgsx.png")
          .name {{__('Rogue Invitational')}}
  template(#right)
    .right-side(:class="{'has-index': indexResults.length > 0}")
      .phrases(v-bind="phrasesContainerAttrs")
        h3.section-title(v-if="suggestions.title") {{suggestions.title}}
        rogue-link.phrase(
          v-for='(phrase, index) in suggestions.phrases'
          :key='phrase.text'
          :to="redirect || `/search?q=${hoveredTerm}`"
          @click="trackSuggestionClick(phrase, index)"
          @mouseover="hoverPhrase(phrase.text)"
          @mouseleave="leavePhrase(phrase.text)"
          v-bind="phraseAttrs(phrase)"
        )
          span(v-html="phrase.html" v-translate)
      .index-results(v-if="indexResults.length > 0")
        h3.section-title.index-title(@click="$router.push('/theindex')") The Index
        rogue-link.phrase(
          v-for='(post,position) in indexResults'
          :key='post.id'
          :to="post.link"
          @click="trackIndexClick(post, position)"
          :data-cnstrc-item-section="SECTIONS.CONTENT"
          :data-cnstrc-item-name="post.slug"
        )
          span(v-html="post.title.rendered" v-translate)
        rogue-link.view-all(v-show="showingIndex && !loadingIndex" :to="'/theindex?q='+query") {{__('View All')}}
</template>
<script setup lang="ts">
import { useRoute } from 'vue-router'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ResultsContainer from '@/js/components/header/search/results-container.vue'
import { type IReflektionProcessedProduct, type IReflektionProcessedResponse, widgets } from '@/js/services/rfk/types'
import { PODS, SECTIONS } from '@/js/services/cnstrc/constants'
import {
  podContainerAttrs,
  useSuggestionColumn,
  SHOP_BY_COUNT,
  type Phrase,
  type RecommendationsMeta,
} from '@/js/components/header/search/useSuggestionColumn'
import { loadPosts } from '@/js/pages/theindex/helpers/search'
import { useRogue } from '@/js/services/Rogue'
import type { ITheIndexPost } from '@/js/pages/theindex/types'

const PREVIEW_ITEM_COUNT = 12
const DEBOUNCE_MS = 250
// The first load waits longer: the dropdown opens before anyone has typed.
const INITIAL_DELAY_MS = 500

/** One search-preview response, plus the pod metadata the containers are attributed with. */
interface SearchPreview {
  response: IReflektionProcessedResponse
  previewMeta: RecommendationsMeta | null
  popular: { phrases: Phrase[], meta: RecommendationsMeta | null }
}

const props = withDefaults(defineProps<{
  query?: string
}>(), {
  query: '',
})

const emit = defineEmits<{
  'update:redirect': [value: string | null],
  'update:phrase': [value: string | null],
}>()

const route = useRoute()
const rogue = useRogue()

const products = ref<Partial<IReflektionProcessedProduct>[]>([])
const indexResults = ref<ITheIndexPost[]>([])
const initialProductSuggestions = ref<Partial<IReflektionProcessedProduct>[]>([])
const initialLoad = ref<boolean>(false)
const emptySearchResults = ref<boolean>(true)
const loading = ref<string | null>(null)
const loadingIndex = ref<string | null>(null)
const showing = ref<string | null>(null)
const showingIndex = ref<string | null>(null)
const redirect = ref<string | null>(null)
const previewSearchMeta = ref<RecommendationsMeta | null>(null)

const {
  suggestions,
  hoveredTerm,
  containerAttrs: phrasesContainerAttrs,
  phraseAttrs,
  setTerms,
  setRelatedSearches,
  clearRelatedSearches,
} = useSuggestionColumn(rogue, computed(() => props.query))

// Read lazily rather than once at setup: the header mounts before the flags may have settled
const useConstructor = computed(() => !!rogue.featureFlags?.isOn('enable_constructor'))
// Strips the absolute host off an RFK redirect so it can be routed internally
const hostRegex = new RegExp(`^.+${rogue.site.hostname.replace('www.', '')}`)

let scheduled: any = undefined
let loadingPreflightQuery: string | null = null
let popularSearches: Promise<SearchPreview['popular']> | undefined

const containerAttrs = computed(() => (previewSearchMeta.value ? {} : { 'data-cnstrc-autosuggest': '' }))

const previewSearchContainerAttrs = computed(() => podContainerAttrs(previewSearchMeta.value))

const invitationalQuery = computed<boolean>(() => {
  if (!props.query || props.query.length === 0) {
    return false
  }

  return ('invitational'.includes(props.query.toLowerCase()) && props.query.length > 2) || props.query.toLowerCase().includes('invitational')
})

// One timer for all three fetches: they are always scheduled and cancelled together
function schedule(delay: number, run: () => void) {
  clearTimeout(scheduled)
  scheduled = setTimeout(run, delay)
}

watch(() => props.query, () => schedule(DEBOUNCE_MS, () => {
  getSearchResults()
  getIndex()
  fetchSearchPreflight()
}))

onMounted(() => {
  products.value = [{}, {}, {}, {}, {}, {}, {}, {}]
  schedule(INITIAL_DELAY_MS, async () => {
    await getSearchResults()
    getIndex().catch(err => {
      console.log('Error loading index', err)
    })
    initialProductSuggestions.value = [...products.value]
  })
})

onBeforeUnmount(() => clearTimeout(scheduled))

async function fetchSearchPreflight(query = props.query) {
  if (!useConstructor.value) return

  if (!query) return clearRelatedSearches()

  loadingPreflightQuery = query
  const preflight = await rogue.cnstrc.getSearchPreflight(query)
  if (query !== loadingPreflightQuery) return
  loadingPreflightQuery = null

  redirect.value = preflight.redirect
  setRelatedSearches(query, preflight.relatedSearches.map(text => ({ text, source: 'related' })), preflight.resultId)
  if (preflight.redirect) {
    emit('update:redirect', preflight.redirect)
  }
}

async function getIndex(query = props.query) {
  if (query && (query === showingIndex.value || query === loadingIndex.value)) {
    return
  }
  loadingIndex.value = query
  indexResults.value = await loadPosts(rogue, { search: query }, { perPage: 4 })
  loadingIndex.value = null
  showingIndex.value = query
}

async function getSearchResults(query = props.query) {
  // Already showing results for this query
  if (query === showing.value || query === loading.value) {
    return
  }

  loading.value = query
  const preview = useConstructor.value ? await fetchConstructorPreview(query) : await fetchRfkPreview(query)

  // Updated again after response received
  if (query !== loading.value) {
    return
  }

  loading.value = null
  showing.value = query
  applyPreview(query, preview)
}

/**
 * The popular-searches pod does not depend on the query, so it is fetched once and reused for
 * the life of the dropdown rather than re-requested on every keystroke.
 */
function loadPopularSearches() {
  popularSearches ??= Promise.resolve(rogue.cnstrc.getRecommendations(PODS.POPULAR_SEARCHES, SHOP_BY_COUNT, SECTIONS.SEARCH_SUGGESTIONS))
    .then(resp => {
      const keyphrases = (resp.suggestion?.keyphrase || []) as { text: string }[]

      return {
        phrases : keyphrases.map(({ text }) => ({ text, source: 'suggestion' as const })),
        meta    : {
          podId      : PODS.POPULAR_SEARCHES,
          resultId   : (resp as any).result_id,
          numResults : keyphrases.length,
        },
      }
    })

  return popularSearches
}

async function fetchConstructorPreview(query: string): Promise<SearchPreview> {
  if (query) {
    const [response, popular] = await Promise.all([rogue.cnstrc.getAutocompleteResults(query, PREVIEW_ITEM_COUNT), loadPopularSearches()])

    // Under a query the popular list is only a fallback, so it is not attributed to its pod
    return { response, previewMeta: null, popular: { phrases: popular.phrases, meta: null } }
  }

  const [response, popular] = await Promise.all([
    Promise.resolve(rogue.cnstrc.getRecommendations(PODS.SEARCH_DEFAULT, PREVIEW_ITEM_COUNT)),
    loadPopularSearches(),
  ])

  return {
    response,
    previewMeta: { podId: PODS.SEARCH_DEFAULT, resultId: (response as any).result_id, numResults: response.total_item },
    popular,
  }
}

async function fetchRfkPreview(query: string): Promise<SearchPreview> {
  const response = await rogue.rfk.fetch({ widget: widgets.SEARCH_PREVIEW, uri: route.path, n_item: PREVIEW_ITEM_COUNT, query })

  return { response, previewMeta: null, popular: { phrases: [], meta: null } }
}

function applyPreview(query: string, { response, previewMeta, popular }: SearchPreview) {
  previewSearchMeta.value = previewMeta

  const found = response.content?.product?.value
  emptySearchResults.value = !found?.length
  // With no results, fall back to the suggestions shown before anything was typed
  products.value = found?.length ? found : initialProductSuggestions.value
  initialLoad.value = true

  // Constructor resolves its redirect on the preflight instead — see fetchSearchPreflight
  if (!useConstructor.value) {
    redirect.value = response.redirect_url?.replace(hostRegex, '') || null
  }
  emit('update:redirect', redirect.value)

  trackView()

  const { category = [], keyphrase = [], trending_category: trending = [] } = response.suggestion || {}
  setTerms({
    query,
    keyphrases  : keyphrase.map(toPhrase),
    categories  : (category.length ? category : trending).map(toPhrase),
    popular     : popular.phrases,
    popularMeta : popular.meta,
  })
}

const toPhrase = (term: { text: string, url?: string }): Phrase => ({ text: term.text, url: term.url, source: 'suggestion' })

function trackView() {
  if (!products.value.every(p => p.productId) || !showing.value) {
    return
  }
  if (props.query) {
    rogue.track.track({
      type             : 'search',
      searchTerm       : props.query,
      searchType       : 'preview',
      numSearchResults : products.value.length,
    })
  }
  rogue.track.track({
    type        : 'listView',
    listType    : 'searchPreview',
    query       : props.query,
    rfkProducts : products.value.map((product, index) => ({
      product: product as IReflektionProcessedProduct,
      index,
    })),
  })
}

function trackClick(product: IReflektionProcessedProduct, index: number) {
  rogue.track.track({
    type     : 'productClick',
    product,
    listType : 'searchPreview',
    position : index,
    page     : 1,
  })
}

function trackSuggestionClick(phrase: Phrase, index: number) {
  rogue.track.track({
    type           : 'searchPreviewSelect',
    query          : props.query,
    suggestion     : phrase.text,
    index,
    suggestionType : 'category',
  })
}

function trackIndexClick(post: ITheIndexPost, position: number) {
  rogue.track.track({ type: 'custom', event: 'index-suggestion-click', data: { link: post.link, position, query: props.query } })
}

function hoverPhrase(phrase: string) {
  hoveredTerm.value = phrase
  emit('update:phrase', phrase)
}

function leavePhrase() {
  if (hoveredTerm.value !== showing.value) {
    hoveredTerm.value = null
    emit('update:phrase', null)
  }
}
</script>
<style lang="scss" scoped>
.products {
  margin: 0 -6px 0 -6px;
  width: 100%;
}

.search-results-container {
  margin-top: $space-s;
}

.title {
  width: 100%;
}

.product {
  color: $color-black;
  display: inline-flex;
  padding: 0 6px 6px 6px;
  flex-direction: column;
  text-decoration: none;
  font-size: $size-xxs;
  width: 33.33%;

  @include not-mobile() {
    width: 25%;
  }
}

.loading {
  opacity: 0.3;
  pointer-events: none;
}

.phrase {
  display: block;
  text-decoration: none;
  color: inherit;
  font-size: $size-s;
  margin-bottom: $space-xs;
  cursor: pointer;
}

@include mobile() {
  .phrase:nth-child(n+4) {
    display: none;
  }

  .search-results-container {
    flex-direction: column-reverse;
  }
}

.has-index {
  .index-results, .phrases {
    @include mobile(){
      width: 50%;
      flex-basis: 50%;
    }
    &.phrases {
      margin-right: $space-s;
    }
  }
}

h3.section-title {
  background-color: black;
  color: white;
  padding: $space-xxs;
  font-size: $size-xs;
}

@include mobile(){
  .index-results {
    margin-bottom: $space-xs;
  }
}

.links {
  display: flex;
  flex-direction: column;
  width: 50%;

  a.invitational {
    text-decoration: none;
    padding-right: 11px;
  }

  .name {
    color: $color-black;
    font-size: $size-xs;
    text-decoration: none;
    font-weight: $weight-bold;
  }
}

.view-all {
  color: $color-black-light;
  margin-top: $space-s;
  font-size: $size-xs;
  margin-bottom: $space-s;
}

.name {
  margin: $space-xxs 0;
}

.no-results {
  text-align: center;
  width: 100%;
}

.suggestion-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.index-title {
  cursor: pointer;
}

@include mobile(){
  .right-side.has-index {
    display: flex;
  }
}
</style>
